/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Modal from '@material-ui/core/Modal';
import Fade from '@material-ui/core/Fade';
import Backdrop from '@material-ui/core/Backdrop';
import { useHistory } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import { Formik } from 'formik';
import TextField from '@material-ui/core/TextField';
import PropTypes from 'prop-types';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { editPassword, setError } from '../store/actions/auth';
import { getErrorMessage } from '../utils';

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: 18,
    margin: 0,
    color: '#1e3f9e',
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    padding: '2rem',
    paddingTop: '1rem',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    boxShadow: theme.shadows[5],
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  sectionHead: {
    width: '100%',
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '2rem',
    width: '100%',
    justifyContent: 'space-around',
  },
  textFields: {
    transform: 'translate(22%, 0%)',
    width: '90%',
    marginLeft: '5%',
    marginRight: '5%',
    marginTop: '0.5rem',
    alignItems: 'center',
    justifyContent: 'center',
  },
  field: {
    width: '60%',
    marginBottom: '1rem',
    marginTop: '1rem',
  },
  error: {
    color: 'red',
    marginTop: '-0.75rem',
    marginBottom: '0.25rem',
  },
  overallError: {
    color: 'red',
    marginLeft: '3.8rem',
    marginTop: '0.5rem',
    marginBottom: '0.25rem',
  },
  button: {
    width: '30%',
    margin: '-1rem 0 1rem 0',
    padding: '0.6rem',
    color: 'white',
    backgroundColor: '#00d1b2',
    '&:hover': {
      backgroundColor: '#00bca0',
    },
  },
  form: {
    margin: '1rem 0 -1rem 0',
    width: '100%',
    maxWidth: 500,
    display: 'flex',
    flexFlow: 'row wrap',
  },
}));

const PasswordChangeModal = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const [displayError, setDisplayError] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const user = useSelector((state) => state.admin.currentUserInfo);
  const stateErrors = useSelector((state) => state.auth.error);

  const handleClose = () => {
    dispatch(setError(null));
    setOpen(false);
  };

  useEffect(() => {
    if (stateErrors) {
      setDisplayError(getErrorMessage(stateErrors));
    }
  }, [stateErrors]);

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = (values) => {
    const payload = {
      username: user.username.toLowerCase(),
      password: values.new_password,
      old_password: values.old_password,
    };
    dispatch(setError(null));
    dispatch(editPassword(payload, history));
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <h2 className={classes.title}>Change Password</h2>
          <Formik
            initialValues={{
              old_password: '',
              new_password: '',
              new_password_confirm: '',
            }}
            validate={(values) => {
              const errors = {};
              if (!values.old_password) {
                errors.old_password = '*Required';
              }
              if (!values.new_password) {
                errors.new_password = '*Required';
              }
              if (!values.new_password_confirm) {
                errors.new_password_confirm = '*Required';
              }
              if (values.new_password_confirm !== values.new_password) {
                errors.newPasswordError = '*Your new passwords are not the same.';
              }
              return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
              setTimeout(() => {
                handleSubmit(values);
                setSubmitting(false);
              }, 400);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              // eslint-disable-next-line no-shadow
              handleSubmit,
              /* and other goodies */
            }) => {
              return (
                <form className={classes.form} onSubmit={handleSubmit}>
                  <div className={classes.sectionHead} />
                  <div className={classes.textFields}>
                    <TextField
                      type={showPassword ? 'text' : 'password'}
                      className={classes.field}
                      label="Current Password"
                      placeholder="Current Password"
                      variant="outlined"
                      name="old_password"
                      value={values.old_password || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={togglePassword} edge="end">
                              <VisibilityIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {errors.old_password && (
                      <p className={classes.error}>{touched.old_password && errors.old_password}</p>
                    )}
                    <TextField
                      type={showPassword ? 'text' : 'password'}
                      className={classes.field}
                      label="New Password"
                      placeholder="Enter New Password"
                      variant="outlined"
                      name="new_password"
                      value={values.new_password || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={togglePassword} edge="end">
                              <VisibilityIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {errors.new_password && (
                      <p className={classes.error}>{touched.new_password && errors.new_password}</p>
                    )}
                    <TextField
                      type={showPassword ? 'text' : 'password'}
                      className={classes.field}
                      label="Confirm New Password"
                      placeholder="Enter New Password"
                      variant="outlined"
                      name="new_password_confirm"
                      value={values.new_password_confirm || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={togglePassword} edge="end">
                              <VisibilityIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {errors.new_password_confirm && (
                      <p className={classes.error}>{touched.new_password_confirm && errors.new_password_confirm}</p>
                    )}
                  </div>
                  {displayError && <p className={classes.overallError}>{displayError}</p>}
                  {errors.newPasswordError && touched.new_password_confirm && touched.new_password && (
                    <p className={classes.overallError}>{errors.newPasswordError}</p>
                  )}
                  <div className={classes.buttonRow}>
                    <Button
                      className={classes.button}
                      size="small"
                      variant="contained"
                      type="submit"
                      onClick={handleSubmit}
                    >
                      Save
                    </Button>
                    <Button className={classes.button} size="small" variant="contained" onClick={handleClose}>
                      Cancel
                    </Button>
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>
      </Fade>
    </Modal>
  );
};

PasswordChangeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};

export default PasswordChangeModal;
